<template>
  <div>
    <section
      class="min-h-screen flex items-center pt-24 pb-12 px-6 relative overflow-hidden bg-[var(--cream)]"
    >
      <div
        class="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center relative z-10 fade-in-content"
      >
        <div class="space-y-6 relative z-20 lg:pr-8">
          <div class="space-y-4">
            <h1
              class="text-6xl md:text-8xl font-bold text-[var(--forest)] leading-tight italic"
            >
              Safe Haven <br /><span
                class="text-[var(--terracotta)] font-normal"
                >Wellness Group</span
              >
            </h1>
            <p
              class="text-lg md:text-xl text-[var(--moss)] max-w-lg font-light leading-relaxed italic"
            >
              Healing through the power of
              <span
                class="font-bold border-b-2 border-[var(--terracotta)] text-[var(--forest)]"
                >Lived Mental Health Experience (LMHE)</span
              >. A space where survival becomes wisdom.
            </p>
            <p class="text-sm text-[var(--moss)] italic">
              Rebranding Towards Lived Mental Health Experience
            </p>
          </div>
          <div class="flex flex-wrap gap-4">
            <button
              @click="openJoinModal"
              class="btn-organic bg-[var(--terracotta)] text-white px-10 py-5 text-lg font-bold shadow-xl hover:bg-[var(--terracotta)]/90"
            >
              Join Our Community
            </button>
          </div>
        </div>
        <div class="relative group z-10 lg:pl-8">
          <div
            class="aspect-square w-full rounded-[60%_40%_70%_30%/50%_60%_40%_50%] overflow-hidden border-8 border-white hero-carousel-container hero-image-enlarged"
          >
            <div
              class="hero-carousel-slide hero-carousel-active"
              style="background-image: url('/images/(28).jpeg')"
            ></div>
            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(31).jpeg')"
            ></div>
            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(110).jpeg')"
            ></div>

            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(109).jpeg')"
            ></div>
            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(108).jpeg')"
            ></div>
            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(30).jpeg')"
            ></div>
            <div
              class="hero-carousel-slide"
              style="background-image: url('/images/(27).jpeg')"
            ></div>
          </div>
          <div
            class="absolute -bottom-8 -left-8 bg-[var(--sand)] p-6 blob-card shadow-xl border border-white/50 hidden md:block z-20"
          >
            <div class="flex items-center gap-4">
              <div class="flex -space-x-3">
                <div
                  class="size-10 rounded-full border-2 border-white bg-cover bg-center"
                  style="background-image: url('/images/(18).jpeg')"
                ></div>
                <div
                  class="size-10 rounded-full border-2 border-white bg-cover bg-center"
                  style="background-image: url('/images/(24).jpeg')"
                ></div>
                <div
                  class="size-10 rounded-full border-2 border-white bg-cover bg-center"
                  style="background-image: url('/images/(11).jpeg')"
                ></div>
              </div>
              <p class="text-sm font-bold text-[var(--forest)] italic">
                500+ Lives Impacted
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--cream)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,192L48,176C96,160,192,128,288,112C384,96,480,96,576,122.7C672,149,768,203,864,213.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="#e6dfd1"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <section
      class="py-16 md:py-32 px-4 md:px-6 bg-[var(--sand)] overflow-hidden"
      id="story"
    >
      <div
        class="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 md:gap-20 items-center fade-in-content"
      >
        <div class="relative order-2 lg:order-1 group overflow-visible">
          <!-- Main history image (swaps with thumbnails) -->
          <div class="history-main-wrap aspect-[4/5] w-full">
            <div
              class="history-main rounded-[40%_60%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-2xl border-4 md:border-8 border-white history-image-blob history-swap-card"
              :style="{ backgroundImage: `url('${historyMainSrc}')` }"
              role="img"
              aria-label="History image"
            ></div>
            <div
              ref="historyOverlayEl"
              class="history-main history-main-overlay rounded-[40%_60%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-2xl border-4 md:border-8 border-white history-image-blob history-swap-card"
              :class="{ 'history-main-overlay-on': historyIsFading }"
              :style="{
                backgroundImage: historyOverlaySrc
                  ? `url('${historyOverlaySrc}')`
                  : 'none',
              }"
              aria-hidden="true"
            ></div>
          </div>

          <!-- Two tiny pictures -->
          <div
            class="history-thumbs absolute -bottom-5 left-6 md:-bottom-8 md:left-10 flex gap-3 z-20"
          >
            <button
              v-for="(thumb, idx) in historyThumbs"
              :key="thumb.id"
              type="button"
              class="history-thumb-btn"
              @click="swapHistoryImage(idx)"
              :aria-label="`Swap main image with ${thumb.id}`"
            >
              <div
                :ref="(el) => setHistoryThumbEl(el, idx)"
                class="history-thumb history-swap-card history-thumb-blob history-image-blob"
                :style="{ backgroundImage: `url('${thumb.src}')` }"
              ></div>
            </button>
          </div>

          <div
            class="absolute -top-6 -right-6 md:-top-10 md:-right-10 size-32 md:size-40 bg-[var(--terracotta)] opacity-20 blob-card -z-10"
          ></div>
        </div>
        <div class="order-1 lg:order-2 space-y-6 md:space-y-8">
          <div class="space-y-3 md:space-y-4">
            <h2
              class="text-[var(--terracotta)] font-bold uppercase tracking-[0.3em] text-xs md:text-sm"
            >
              Founding Journey
            </h2>
            <h3
              class="text-3xl md:text-5xl lg:text-6xl font-bold text-[var(--forest)] italic break-words"
            >
              Our History &amp; Legacy
            </h3>
            <div
              class="w-24 md:w-32 h-1 md:h-1.5 bg-[var(--terracotta)] rounded-full"
            ></div>
          </div>
          <div
            class="space-y-4 md:space-y-6 text-base md:text-lg text-[var(--moss)] font-light leading-relaxed italic"
          >
            <p class="break-words">
              Safe Haven Wellness Group is a nonprofit, voluntary therapy
              collective founded by
              <strong class="text-[var(--forest)] font-semibold"
                >Nadra Aley</strong
              >, a certified life coach inspired by her lived experience with
              mental health challenges, particularly involving suicide. Through
              the support of peer-led groups, Nadra recognized the power of
              shared experiences and sought to create a safe, supportive space
              where those navigating mental health journeys could come together
              for healing, learning, and growth.
            </p>
            <p class="break-words">
              What began as Nadra's personal mission has evolved into a
              community anchored in lived mental health experience (LMHE).
              Today, Safe Haven Wellness Group includes three passionate
              co-founders with diverse backgrounds in counseling psychology,
              youth advocacy, and maternal mental health. Together, they are
              building a platform that not only supports healing but elevates
              the voices of those with LMHE—ensuring they are represented in
              research, policy, and intervention spaces.
            </p>
            <p class="break-words">
              Our journey began as a small circle in a living room and has grown
              into a structured collective that bridges clinical excellence with
              the raw honesty of shared struggle. We don't just treat symptoms;
              we honor the human behind the diagnosis.
            </p>
          </div>
          <router-link
            to="/bio"
            class="btn-organic bg-[var(--forest)] text-white px-6 md:px-10 py-3 md:py-4 text-sm md:text-base font-bold w-full md:w-auto inline-block text-center"
            >Read More</router-link
          >
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--sand)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,117.3C960,107,1056,149,1152,165.3C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="#f9f5f0"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <!-- Stats Section -->
    <section class="py-24 md:py-32 px-6 bg-[var(--cream)]" id="stats">
      <div class="max-w-6xl mx-auto fade-in-content">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          <div
            v-for="(stat, idx) in stats"
            :key="idx"
            class="text-center stat-item fade-in-item"
            :ref="(el) => setStatRef(el, idx)"
          >
            <div
              class="text-5xl md:text-6xl lg:text-7xl font-bold text-[var(--terracotta)] italic mb-2 stat-number"
            >
              {{ animatedStats[idx] }}{{ stat.suffix }}
            </div>
            <div
              class="text-sm md:text-base text-[var(--moss)] italic font-light"
            >
              {{ stat.label }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--cream)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,192L48,176C96,160,192,128,288,112C384,96,480,96,576,122.7C672,149,768,203,864,213.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="var(--moss)"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <!-- Why Safe Haven Exists Section -->
    <section class="py-32 px-6 bg-[var(--moss)] text-[var(--cream)]">
      <div class="max-w-6xl mx-auto fade-in-content">
        <div class="text-center max-w-3xl mx-auto space-y-8">
          <div class="space-y-4">
            <h2
              class="text-[var(--terracotta)] font-bold uppercase tracking-[0.3em] text-sm"
            >
              Our Purpose
            </h2>
            <h3 class="text-5xl md:text-6xl font-bold italic">
              Why Safe Haven Exists
            </h3>
            <div
              class="w-32 h-1.5 bg-[var(--terracotta)] rounded-full mx-auto"
            ></div>
          </div>
          <div
            class="space-y-6 text-lg text-[var(--sage)] font-light leading-relaxed italic"
          >
            <p>
              Safe Haven exists to challenge the isolation, stigma, and silence
              often surrounding mental health. In a world where mental
              well-being is frequently overlooked, especially in marginalized
              communities, Safe Haven empowers individuals with lived experience
              to lead conversations, influence solutions, and shape a future
              where healing is dignified and inclusive.
            </p>
            <p>
              It is a sanctuary for those who need support and a resource hub
              for those seeking to engage ethically and effectively with LMHE.
            </p>
          </div>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--moss)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,192L48,176C96,160,192,128,288,112C384,96,480,96,576,122.7C672,149,768,203,864,213.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="#2d4635"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <!-- Partners Section -->
    <section class="py-20 bg-[var(--forest)] text-[var(--cream)]" id="partners">
      <div class="max-w-6xl mx-auto fade-in-content">
        <div class="text-center mb-16 space-y-4 px-6">
          <h2 class="text-4xl md:text-5xl font-bold text-[var(--cream)] italic">
            Our Partners
          </h2>
          <p class="text-lg md:text-xl text-[var(--sage)] italic max-w-2xl mx-auto">
            Organizations and institutions working alongside us to create
            meaningful change.
          </p>
        </div>

        <!-- Partners Grid -->
        <div class="flex flex-wrap justify-center items-center gap-8 px-6">
          <div
            v-for="(partner, index) in partnersList"
            :key="index"
            class="group transition-all duration-300 hover:scale-110 cursor-pointer"
            @click="openPartnerLink(partner.link)"
          >
            <img
              :src="partner.image"
              :alt="partner.name"
              class="w-24 h-24 md:w-32 md:h-32 object-contain opacity-80 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </div>

        <!-- CTA for Partner -->
        <div class="text-center px-6 mt-12">
          <button
            @click="openPartnerEmail"
            class="btn-organic bg-[var(--cream)] text-[var(--forest)] text-sm font-bold shadow-lg"
          >
            Join as Partner
          </button>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--forest)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,192L48,176C96,160,192,128,288,112C384,96,480,96,576,122.7C672,149,768,203,864,213.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="var(--forest)"
          fill-opacity="1"
        ></path>
      </svg>
    </div>


    <!-- Scroll to top button -->
    <Transition name="scroll-top">
      <button
        v-if="showScrollToTop"
        @click="scrollToTop"
        class="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 w-12 h-12 bg-[var(--forest)] text-white rounded-full shadow-xl hover:bg-[var(--forest)]/90 hover:scale-110 active:scale-95 transition-all flex items-center justify-center group cursor-pointer"
        aria-label="Scroll to top"
      >
        <span
          class="material-symbols-outlined text-2xl group-hover:-translate-y-1 transition-transform"
          >arrow_upward</span
        >
      </button>
    </Transition>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref } from "vue";

export default {
  name: "HomePage",
  setup() {
    const currentSlide = ref(0);
    const showScrollToTop = ref(false);

    // Stats data
    const stats = ref([
      { value: 500, label: "Lives Impacted", suffix: "+" },
      { value: 25, label: "Active Volunteers", suffix: "+" },
      { value: 50, label: "Peer Support Sessions", suffix: "+" },
      { value: 12, label: "Community Programs", suffix: "+" },
    ]);
    const statRefs = ref([]);
    const animatedStats = ref([0, 0, 0, 0]);

    const setStatRef = (el, idx) => {
      if (el) {
        statRefs.value[idx] = el;
      }
    };

    // Animate stats on scroll
    const animateStats = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry, idx) => {
            if (entry.isIntersecting) {
              const stat = stats.value[idx];
              if (!stat) return;

              const targetValue = stat.value;
              const duration = 2000; // 2 seconds
              const steps = 60;
              const increment = targetValue / steps;
              const stepDuration = duration / steps;
              let current = 0;

              const timer = setInterval(() => {
                current += increment;
                if (current >= targetValue) {
                  animatedStats.value[idx] = targetValue;
                  clearInterval(timer);
                } else {
                  animatedStats.value[idx] = Math.floor(current);
                }
              }, stepDuration);

              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: "0px",
        },
      );

      statRefs.value.forEach((ref) => {
        if (ref) {
          observer.observe(ref);
        }
      });
    };


    // History image swap (main <-> thumbnails)
    const historyMainSrc = ref("/images/nadra-pic.webp");
    const historyThumbs = ref([
      { id: "guy", src: "/images/guy.jpeg" },
      { id: "rashid", src: "/images/rashid.jpeg" },
    ]);
    const historyOverlaySrc = ref("");
    const historyIsFading = ref(false);
    const historyOverlayEl = ref(null);
    const historyIsAnimating = ref(false);

    const setHistoryThumbEl = (el, idx) => {
      // kept for backwards compatibility (no-op for now)
      void el;
      void idx;
    };

    const swapHistoryImage = async (idx) => {
      if (historyIsAnimating.value) return;
      const thumb = historyThumbs.value[idx];
      if (!thumb) return;

      historyIsAnimating.value = true;

      const waitTransitionEnd = (el) =>
        new Promise((resolve) => {
          if (!el) return resolve();
          const onEnd = (e) => {
            if (e.target !== el) return;
            el.removeEventListener("transitionend", onEnd);
            resolve();
          };
          el.addEventListener("transitionend", onEnd);
        });

      try {
        const oldMain = historyMainSrc.value;

        historyOverlaySrc.value = thumb.src;
        historyIsFading.value = true;
        await waitTransitionEnd(historyOverlayEl.value);

        // Commit swap after fade completes
        historyMainSrc.value = thumb.src;
        historyThumbs.value[idx] = { ...thumb, src: oldMain };
      } finally {
        historyIsFading.value = false;
        historyOverlaySrc.value = "";
        historyIsAnimating.value = false;
      }
    };

    // Partners list with logos
    const partnersList = ref([
      {
        name: "Nivishe Foundation",
        image: "/images/nivishe.png",
        link: "https://nivishefoundation.org/",
      },
      {
        name: "Seeds of Change Initiative",
        image:
          "https://seedsofchange.co.ke/wp-content/uploads/2019/03/cropped-Seeds-of-Change-logo-80px-1-1.png",
        link: "https://seedsofchange.co.ke/",
      },
      {
        name: "Nadra's Life Coaching",
        image: "/images/nadra-life-coach-logo.png",
        link: "https://www.nadraslifecoaching.com/",
      },
    ]);

    
    const handleScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset;

      // Show/hide scroll to top button
      showScrollToTop.value = currentScrollY > 300;
    };

    const openJoinModal = () => {
      window.dispatchEvent(new CustomEvent("open-join-modal"));
    };

    // Open partner email template
    const openPartnerLink = (link) => {
      if (link && typeof window !== 'undefined' && window.open) {
        window.open(link, '_blank');
      }
    };


    const openPartnerEmail = () => {
      const subject = encodeURIComponent(
        "Partnership Inquiry - Safe Haven Wellness Group",
      );
      const body = encodeURIComponent(`Dear Safe Haven Wellness Group Team,

I am interested in exploring a partnership opportunity with Safe Haven Wellness Group.

Organization/Institution: [Your Organization Name]
Contact Person: [Your Name]
Email: [Your Email]
Phone: [Your Phone Number]

Partnership Interest:
[Please describe your interest in partnering with Safe Haven Wellness Group]

How we can collaborate:
[Please describe potential collaboration opportunities]

Best regards,
[Your Name]`);
      const emailUrl = `mailto:amisarashid37@gmail.com?subject=${subject}&body=${body}`;
      window.location.href = emailUrl;
    };


    // Scroll to top function
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    // Lazy load images with fade-in
    const setupLazyLoading = () => {
      const imageContainers = document.querySelectorAll(
        ".lazy-image-container",
      );
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const container = entry.target;
              const imgDiv = container.querySelector(".lazy-image");
              if (imgDiv && !imgDiv.classList.contains("loaded")) {
                const imageSrc = imgDiv.getAttribute("data-src");
                if (imageSrc) {
                  const img = new Image();
                  img.onload = () => {
                    imgDiv.style.backgroundImage = `url('${imageSrc}')`;
                    imgDiv.classList.add("loaded");
                    container.classList.add("fade-in");
                  };
                  img.src = imageSrc;
                }
              }
              observer.unobserve(container);
            }
          });
        },
        {
          rootMargin: "50px",
        },
      );

      imageContainers.forEach((container) => observer.observe(container));
    };

    // Staggered fade-in for content and items
    const setupSectionFadeIn = () => {
      // Observer for main content containers
      const contentElements = document.querySelectorAll(".fade-in-content");
      let visibleContentCount = 0;

      const contentObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleContentCount * 150;
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleContentCount++;
              contentObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-10% 0px -10% 0px",
          threshold: 0.1,
        },
      );

      contentElements.forEach((content) => {
        contentObserver.observe(content);
      });

      // Observer for individual items (cards, columns, etc.)
      const itemElements = document.querySelectorAll(".fade-in-item");
      let visibleItemCount = 0;

      const itemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleItemCount * 100; // 100ms delay between items for faster stagger
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleItemCount++;
              itemObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-5% 0px -5% 0px", // Trigger earlier for items
          threshold: 0.1,
        },
      );

      itemElements.forEach((item) => {
        itemObserver.observe(item);
      });
    };

    // Hero carousel functionality
    const startHeroCarousel = () => {
      const slides = document.querySelectorAll(".hero-carousel-slide");
      if (slides.length === 0) return;

      setInterval(() => {
        slides.forEach((slide) => {
          slide.classList.remove("hero-carousel-active", "hero-carousel-zoom");
        });

        currentSlide.value = (currentSlide.value + 1) % slides.length;

        const nextSlide = slides[currentSlide.value];
        if (nextSlide) {
          nextSlide.classList.add("hero-carousel-active");
          // Trigger zoom effect
          setTimeout(() => {
            nextSlide.classList.add("hero-carousel-zoom");
          }, 100);
        }
      }, 5000); // Change slide every 5 seconds
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });

      // Setup lazy loading
      setupLazyLoading();

      // Setup section fade-in animations
      setupSectionFadeIn();

      // Start hero carousel
      startHeroCarousel();

      // Setup stats animation
      setTimeout(() => {
        animateStats();
      }, 500);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      currentSlide,
      showScrollToTop,
      scrollToTop,
      openJoinModal,
      openPartnerEmail,
      openPartnerLink,
      historyMainSrc,
      historyThumbs,
      setHistoryThumbEl,
      swapHistoryImage,
      historyOverlayEl,
      historyOverlaySrc,
      historyIsFading,
      partnersList,
      stats,
      animatedStats,
      setStatRef,
    };
  },
};
</script>

<style scoped>
.pillar-card {
  position: relative;
  transition: border-radius 0.5s ease-in-out;
}

.pillar-card:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.pillar-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.pillar-card:hover .pillar-icon-bg {
  transform: translate(-50%, -50%) scale(1.1);
  opacity: 0.65;
}

.pillar-icon-bg::before {
  content: "";
  font-family: "Material Symbols Outlined";
  font-size: 150px;
  line-height: 1;
  color: var(--sand);
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
}

.pillar-icon-1::before {
  content: "diversity_3";
}

.pillar-icon-2::before {
  content: "psychology";
}

.pillar-icon-3::before {
  content: "volunteer_activism";
}

.goal-card {
  position: relative;
  transition: border-radius 0.5s ease-in-out;
}

.goal-card:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.goal-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.25;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.goal-card:hover .goal-icon-bg {
  transform: translate(-50%, -50%) scale(1.1);
  opacity: 0.35;
}

.goal-icon-bg::before {
  content: "";
  font-family: "Material Symbols Outlined";
  font-size: 150px;
  line-height: 1;
  color: var(--terracotta);
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
}

.goal-icon-1::before {
  content: "meeting_room";
}

.goal-icon-2::before {
  content: "schema";
}

.goal-icon-3::before {
  content: "campaign";
}

.goal-icon-4::before {
  content: "groups";
}

.goal-icon-5::before {
  content: "psychology";
}

.goal-icon-6::before {
  content: "handshake";
}

/* Scroll to top button transitions */
.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: all 0.3s ease-out;
}

.scroll-top-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

/* Video modal transitions */
.modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.modal-leave-active {
  transition: opacity 0.3s ease-in;
}

/* Staggered fade-in content */
.fade-in-content {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-content.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Staggered fade-in items */
.fade-in-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-item.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Lazy loading fade-in */
.lazy-image-container {
  opacity: 0;
  transition: opacity 0.8s ease-in-out;
}

.lazy-image-container.fade-in {
  opacity: 1;
}

.lazy-image {
  opacity: 0;
  transition: opacity 0.8s ease-in-out;
}

.lazy-image.loaded {
  opacity: 1;
}


/* History image blob hover effect */
.history-image-blob {
  transition: border-radius 0.5s ease-in-out;
}

.group:hover .history-image-blob {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

/* History swap visuals */
.history-swap-card {
  background-size: cover;
  background-position: center;
}

.history-main-wrap {
  position: relative;
}

.history-main {
  position: absolute;
  inset: 0;
}

.history-main-overlay {
  opacity: 0;
  pointer-events: none;
  transition: opacity 260ms ease;
}

.history-main-overlay-on {
  opacity: 1;
}

.history-thumb-btn {
  display: inline-flex;
  padding: 0;
  border-radius: 9999px;
}

.history-thumb-btn:focus-visible {
  outline: 3px solid rgba(45, 70, 53, 0.6);
  outline-offset: 4px;
  border-radius: 9999px;
}

.history-thumb {
  width: 56px;
  height: 56px;
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  transition: transform 200ms ease, box-shadow 200ms ease;
  transform: rotate(-3deg);
}

@media (min-width: 768px) {
  .history-thumb {
    width: 72px;
    height: 72px;
  }
}

.history-thumb-btn:hover .history-thumb {
  transform: rotate(-10deg) scale(1.06);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
}

.history-thumb-blob {
  /* More rugged blob at small size (still consistent with main's organic feel) */
  border-radius: 35% 65% 25% 75% / 60% 25% 75% 40%;
  overflow: hidden;
}

/* Hero carousel */
.hero-carousel-container {
  position: relative;
}

.hero-image-enlarged {
  transform: none;
  transform-origin: center;
}

@media (min-width: 1024px) {
  .hero-image-enlarged {
    /* Grow the window, but bias the growth to the right so it doesn't collide with left text */
    transform: translateX(10%) scale(1.4);
    transform-origin: center;
  }
}

.hero-carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1);
  transition: opacity 1s ease-in-out, transform 8s ease-in-out;
}

.hero-carousel-slide.hero-carousel-active {
  opacity: 1;
  z-index: 1;
}

.hero-carousel-slide.hero-carousel-zoom {
  transform: scale(1.1);
}

</style>
